import axios from "axios";
import feedback  from "@/components/ui/general/feedback/feedback";
import useGameStore from '@store/useGameStore';
import useSelectionStore from '@store/useSelectionStore';
import useAuthStore from '@store/useAuthStore';
import normalizeGameData from '@utils/normalizeGameData';
import { CONFIG } from '@utils/constants';
import { validateUnits } from '@utils/unitValidator';
import { Asset } from '@rts';

const api = axios.create({
  baseURL: CONFIG.API_URL,
});

// Configuración de Axios para manejar errores y añadir el token de autorización
// Si la URL base no está definida, lanzamos un error.
if (!api.defaults.baseURL) {
  throw new Error("API base URL is not defined. Please check CONFIG.API_URL in constants.js.");
}


api.interceptors.request.use(config => {

  const token = localStorage.getItem('token');
  if (token) {
    // Si hay un token, lo añadimos a la cabecera de Authorization.
     //api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
     config.headers.Authorization = `Bearer ${token}`;
  }else
  {
    console.warn("🚀 Configuración de solicitud Axios no hay Bearer Token:", {
      url: config.url,
      method: config.method,
      headers: config.headers,
    });
  }

  return config;
}, error => {
  return Promise.reject(error);
});

api.interceptors.response.use(
  response => {
    const data = response.data;

    // Check for redirect_to_login action
    if (data && data.action === 'redirect_to_login') {
      // Clear auth state
      useAuthStore.getState().clearAuthState();
      // Show message to user
      if (data.userMessage) {
        feedback.warning(data.userMessage);
      }
      // Only redirect if NOT already on login page (prevent infinite loop)
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
      // Return rejected promise to stop further processing
      return Promise.reject(new Error('Session expired'));
    }

    // ✅ --- RTS: THEME & ASSETS HANDLING --- ✅
    // 1. Theme config (solo en login/register)
    if (data && data.theme) {
      try {
        Asset.setTheme(data.theme);
      } catch (error) {
        console.error('Failed to load theme:', error);
      }
    }

    // 2. Assets a precargar
    if (data && data._assets) {
      try {
        Asset.preload(data._assets);
      } catch (error) {
        console.error('Failed to queue assets:', error);
      }
    }

    // ✅ --- ¡LA LÓGICA DE SINCRONIZACIÓN CENTRALIZADA! --- ✅
    // Si la respuesta de la API contiene el objeto xhelos, actualizamos el store.
    if (data && data.data && data.data.xhelos) {
      // Normalize the game data ONCE here
      const normalizedGame = normalizeGameData(data.data.xhelos);
      
      // Validate units based on game status (only in debug mode)
      if (CONFIG.DEBUG_MODE) {
        const gameStatus = normalizedGame.status;
        
        // For game_plan and game_deploy phases - only validate current snapshot
        if (gameStatus === 'game_plan' || gameStatus === 'game_deploy') {
          if (normalizedGame.snapshots?.current?.dynamic_objects?.units) {
            validateUnits(normalizedGame.snapshots.current.dynamic_objects.units, 'API current snapshot (plan/deploy)');
          } else {
            console.error('[API Validator] Missing units in current snapshot for', gameStatus, 'phase');
          }
        }
        
        // For game_resolved phase - validate both base and current
        if (gameStatus === 'game_resolved') {
          // Validate base snapshot
          if (normalizedGame.snapshots?.base?.dynamic_objects?.units) {
            validateUnits(normalizedGame.snapshots.base.dynamic_objects.units, 'API base snapshot (resolved)');
          } else {
            console.error('[API Validator] Missing units in base snapshot for game_resolved phase');
          }
          
          // Validate current snapshot
          if (normalizedGame.snapshots?.current?.dynamic_objects?.units) {
            validateUnits(normalizedGame.snapshots.current.dynamic_objects.units, 'API current snapshot (resolved)');
          } else {
            console.error('[API Validator] Missing units in current snapshot for game_resolved phase');
          }
        }
      }
      
      // Clear selections if loading a DIFFERENT game (prevents stale unit references)
      const currentGameId = useGameStore.getState().game?.id;
      const newGameId = normalizedGame.id;
      if (currentGameId !== newGameId) {
        useSelectionStore.getState().clearAllSelections();
      }

      // Call setGame directly from the store
      useGameStore.getState().setGame(normalizedGame);
    }
    else{
      //console.warn("[API]No game data found in response:", response);
    }


    const type = (data.status || 'success').toLowerCase();
    const userMsg = data.userMessage;
    const techMsg = data.message || '⚠️ No hay mensaje tecnico';

    // Mensaje técnico siempre
    if (techMsg) {
      //console.log(`📨 ${type.toUpperCase()} (${url}): ${techMsg}`);
    }

    // Feedback visible solo si está permitido
    if (data.userVisible !== false) {
      if (userMsg) {
        if (feedback[type]) {
          // Build options object with messageType and messageOptions from backend
          const feedbackOptions = {
            messageType: data.messageType || 'panel', // Default to panel for backward compatibility
            messageOptions: data.messageOptions || {}
          };
          feedback[type](`${userMsg}`, feedbackOptions);
        } else {
          feedback.warning(`⚠️ Tipo de feedback desconocido: "${type}"`);
        }
      } else {
        // No mostramos advertencia si no hay mensaje de usuario, es un comportamiento esperado.
      }
    }

    // --- ¡LA CORRECCIÓN CLAVE ESTÁ AQUÍ! ---
    // En lugar de devolver el objeto 'response' completo, devolvemos solo 'response.data'.
    // 'response.data' es el cuerpo JSON que envía nuestro backend (el GameResponseDTO).
    return response.data;
  },

  error => {
    const res = error.response;

    if (!res) {
      feedback.error("❌ Network error or no response from server.");
      console.error("❌ Network error or no response from server.");
      return Promise.reject(error);
    }

    const data = res.data || {};
    const status = res.status;
    const url = res.config?.url || '';
    const userMsg = data.userMessage || null;
    const techMsg = data.message || 'Unknown error';
    const type = (data.status || 'error').toLowerCase();

     // 👇 Casos específicos (tu lógica original, mantenida)
    switch (status) {
      case 401:
        if (url.includes("/api/user")) {
          // Don't show message for initial user check - this is expected
          console.warn(`🔒 ${status} - Requiere autenticación para: ${url}`);
        } else {
          // For other 401 errors: clear auth state and redirect to login
          console.error(`🔒 ${status} - Sesión inválida o expirada: ${url}`);

          // Clear authentication state
          useAuthStore.getState().clearAuthState();

          // Show message to user
          feedback.warning(`🔒 Sesión expirada. Por favor, inicia sesión nuevamente.`);

          // Redirect to login (only if not already there)
          if (window.location.pathname !== '/') {
            window.location.href = '/';
          }
        }
        break;

      case 404:
        // Check if it's a game-related 404 (game was deleted/doesn't exist)
        if (url.match(/\/api\/games\/\d+/)) {
          console.warn(`🎮 Game not found: ${url} - cleaning up and redirecting to dashboard`);

          // Clear game state from store
          useGameStore.getState().clearGame();
          useSelectionStore.getState().clearAllSelections();

          // Clear any stored game ID from localStorage
          localStorage.removeItem('currentGameId');

          // Show friendly message
          feedback.warning('El juego ya no existe. Redirigiendo al dashboard...');

          // Redirect to dashboard (only if not already there)
          if (window.location.pathname !== '/dashboard') {
            setTimeout(() => {
              window.location.href = '/dashboard';
            }, 1500); // Small delay so user sees the message
          }
        } else {
          feedback.error(`❌ ${userMsg || 'Recurso no encontrado'}: ${url}`);
          console.warn(`⚠️ ${status} - No encontrado: ${techMsg}`);
        }
        break;

      case 422:
        // Laravel validation error - extract validation messages
        console.warn(`⚠️ ${status} - Validation error: ${techMsg}`);
        if (data.errors) {
          // Get first error message from validation errors
          const firstField = Object.keys(data.errors)[0];
          const validationMessage = data.errors[firstField]?.[0] || techMsg;
          // Attach validation errors to the error object for component-level handling
          data.validationErrors = data.errors;
          data.userMessage = validationMessage;
          feedback.warning(validationMessage);
        } else if (techMsg) {
          data.userMessage = techMsg;
          feedback.warning(techMsg);
        }
        break;

      default:
        console.error(`💥 [API ${status}] ${url}`, {
          message: techMsg,
          userMessage: userMsg,
          errorCode: data.errorCode,
          data: data.data
        });
        // Build feedback options from backend response
        const feedbackOptions = {
          messageType: data.messageType || 'panel',
          messageOptions: data.messageOptions || {}
        };
        if (status >= 500) {
          feedback.error(userMsg || 'Server Error. Try later.', feedbackOptions);
        } else if (status >= 400) {
          if (userMsg) {
            if (feedback[type]) {
              feedback[type](userMsg, feedbackOptions);
            } else {
              feedback.warning(`⚠️ Tipo de feedback desconocido: "${type}"`, feedbackOptions);
            }
          } else {
            console.warn("⚠️ No user message provided in response data.", url);
          }
        }
        break;
    }

    // Rechazamos la promesa con el cuerpo del error, para poder usarlo en los 'catch'.
    return Promise.reject(data);
  }
);

export default api;